import { Plus, Images, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { money } from "../lib/format";

export default function ProductCard({ product, currency, tall = false, onOpen }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const hasOffer = product.is_offer && product.offer_price;
  const galleryCount = product.images?.length || 0;
  const fav = has(product.id);

  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col"
    >
      <div
        onClick={() => onOpen && onOpen(product)}
        className={`relative overflow-hidden bg-surface cursor-pointer ${
          tall ? "aspect-[3/4]" : "aspect-[4/5]"
        }`}
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
        />

        {/* badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {hasOffer && (
            <span className="bg-rose text-cream text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">
              Oferta
            </span>
          )}
          {product.is_upcoming && (
            <span className="bg-espresso text-cream text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">
              Próximamente
            </span>
          )}
        </div>

        {galleryCount > 1 && (
          <span className="absolute bottom-3 left-3 flex items-center gap-1 bg-cream/85 backdrop-blur text-espresso text-[10px] px-2 py-1 rounded-full">
            <Images size={11} /> {galleryCount}
          </span>
        )}

        {/* wishlist heart */}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={(e) => {
            e.stopPropagation();
            toggle(product);
          }}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-cream/85 backdrop-blur flex items-center justify-center hover:scale-110 transition-transform"
          aria-label="Guardar en favoritos"
        >
          <Heart
            size={16}
            className={fav ? "fill-rose text-rose" : "text-espresso"}
          />
        </button>

        {/* add to cart */}
        {!product.is_upcoming && (
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={(e) => {
              e.stopPropagation();
              addItem(product);
            }}
            className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-cream/90 backdrop-blur text-espresso flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 hover:bg-rose hover:text-cream"
            aria-label={`Agregar ${product.name} al carrito`}
          >
            <Plus size={18} />
          </button>
        )}
      </div>

      <div
        onClick={() => onOpen && onOpen(product)}
        className="mt-4 flex items-start justify-between gap-3 cursor-pointer"
      >
        <div>
          <h3 className="font-serif text-lg leading-tight text-espresso">{product.name}</h3>
          {product.description && (
            <p className="text-xs text-taupe mt-1 line-clamp-2 max-w-[240px] font-light">
              {product.description}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          {hasOffer ? (
            <>
              <p className="text-sm text-rose font-medium">
                {money(product.offer_price, currency)}
              </p>
              <p className="text-xs text-taupe line-through">
                {money(product.price, currency)}
              </p>
            </>
          ) : (
            <p className="text-sm text-espresso font-medium">
              {money(product.price, currency)}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
